// La pantalla del modo Redes — ADR-2026-10-01-modo-redes.
//
// Una sola pantalla con dos estados:
//
//  • **Portada** (`/redes`): la caja de prompt arriba y las plantillas debajo. Generar
//    CREA la sesión —una fila de `agent_chats` con `tipo = 'redes'`, como un diseño
//    (ADR-2026-09-17-un-diseno-es-un-chat)— y pasa a su URL.
//  • **Sesión** (`/redes/:sesionId`): el lienzo del Agente arriba, con su barra de
//    herramientas en vertical a la derecha, y la caja abajo. Cada Generar cuelga una tanda
//    del lienzo. "Plantillas", abajo a la derecha, esconde el lienzo y vuelve a la vista de
//    la portada sin salir de la sesión.
//
// Que la sesión sea un chat es lo que hace que "Ayuda de prompt" sirva: el panel lateral
// abre la MISMA conversación, así que el agente ve el lienzo, y la pantalla se registra en
// el puente para que lea y escriba el prompt (`describirRedes`).
//
// La generación es la de los modos de origen (`generarEnRedes`); acá sólo se decide a qué
// sesión va y se cuida el saldo y el plan antes de pedir.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { AlertCircle, ChevronDown, Clapperboard, LayoutGrid, Lock, MessageCircle, Minimize2, PanelsTopLeft, Plus } from 'lucide-react';
import { estadoDesdeRedes, proyectoDeRedes } from '../../../../lib/estudioDesdeRedes';
import { recordarGeneracionEnRedes, vistaAlEntrar } from './inactividadDeRedes';
import SubscriptionLimitModal from '../../../../components/SubscriptionLimitModal';
import { ANCHO_LOGO_SIDEBAR_MINIMA, useSidebar } from '../../../../context/SidebarContext';
import { useChatAtlas, useChatAtlasCascaron } from '../../../../chat-atlas/ChatAtlasContext';
import { MensajesDeLaConversacion, useRegistrarPantalla, type ControladorDePantalla } from '../../../../chat-atlas';
import { PastillasDeContenido } from '../../../../chat-atlas/ui/PastillasDeContenido';
import { useAltoDelTeclado } from '../../../../chat-atlas/ui/useAltoDelTeclado';
import { EVENTO_CHAT_TITULADO, type DetalleChatTitulado } from '../../tituloDeChat';
import { LienzoDelAgente } from '../../../../chat-atlas/lienzo/LienzoDelAgente';
import { hayGeneracionEnVuelo } from '../../../../chat-atlas/lienzo/tandasDelLienzo';
import { useLienzoDelChat } from '../../../../chat-atlas/lienzo/useLienzoDelChat';
import { consultarSaldo, tipoDeLimite, type TipoDeLimite } from '../../../../chat-atlas/lienzo/herramientas/saldo';
import { ModalDeFuentes, type IdDeFuente } from '../../../../chat-atlas/ui/ModalDeFuentes';
import { useAccesoAlAgente } from '../../../../chat-atlas/ui/useAccesoAlAgente';
import { useUserGroup } from '../../../../hooks/useUserGroup';
import { estadoSinMedio, medioDeEntrada, modoDeEntrada } from './entradaACrear';
import { getModePlanLock, type ModePlanLock } from '../../../../lib/planCapabilities';
import { getUserPlan } from '../../../../lib/planUtils';
import { signReferenceImage, uploadReferenceImage, type ChatAttachment } from '../../agentAttachments';
import { VeloSoltarImagen, usePegarYSoltarImagenes } from '../shared/usePegarYSoltarImagenes';
import { conCalidad } from '../creadorVideo/creadorVideo';
import type { ProveedorDeImagen } from '../../../../lib/proveedoresDeImagen';
import { medirDuracionDeVideo } from '../../../../utils/medirDuracionDeVideo';
import { TIPO_CHAT_REDES, createChat, leerTituloDeChat, renombrarChat, sesionDeRedesVaciaParaReusar } from '../../agentChatApi';
import { useAgentEnv } from '../../agentEnvContext';
import { useUserModels } from '../../modelsContext';
import { useTandasPanel } from '../../useGeneracionesEnCurso';
import { PanelTandaCreaciones } from '../../components/PanelTandaCreaciones';
import { GeneratedImagesHistory } from '../../../GeneratedImagesHistory';
import { leerVisorDelLienzo, registrarVisorDelLienzo } from '../../visorDelLienzo';
import { leerAgregarDelChat, registrarAgregarDelChat, type AgregarDelChat } from '../../agregarDelChat';
import type { GeneracionDelegada } from '../../components/generacionDelegada';
import { preloadGeneratedImages, type PreloadedImage } from '../../../../utils/preloadGeneratedImages';
import { preloadVideosDeTanda } from '../../../../utils/preloadVideosDeTanda';
import { sonarListo } from '../../../../utils/sonarListo';
import { CajaDeRedes } from './CajaDeRedes';
import { FondoDeCrear } from './FondoDeCrear';
import { DemoDelAsistente } from './DemoDelAsistente';
import { AlturaSuave } from './AlturaSuave';
import { CarruselDePlantillas } from './CarruselDePlantillas';
import { HistorialDeSesiones } from './HistorialDeSesiones';
import { conTransicion } from './conTransicion';
import { rubroDeCrear } from './contenidoPorRubro';
import { useRubroMarcaActiva } from '../../../../hooks/useRubroMarca';
import { useEsEscritorio } from '../../../../chat-atlas/ui/useEsEscritorio';
import { latido, volarHacia } from './volarHacia';
import { PlantillasDeRedes, type PlantillaElegida } from './PlantillasDeRedes';
import { CLAVE_PANTALLA_REDES, aplicarEnRedes, describirRedes } from './describirRedes';
import { deImagenANombre } from './mencionesDeVideo';
import { ajustesConReceta, leerReceta } from './recetaDeRedes';
import { nombreDeMencion } from '../../mencionesDeAdjuntos';
import { MAX_REFERENCIAS_VIDEO } from '../../referenciasDeGeneracion';
import { ordenDeReferencias } from '../promptLibre/promptLibre';
import { escribirPromptEnRedes, fuenteDeAnimar, pedirEnRedes, type TandaPedida } from './generarEnRedes';
import { fetchSuggestedVideoPromptForSource } from '../../../../lib/videoFromImage';
import {
  conModo,
  esAnimar,
  esAutomatico,
  AJUSTES_DEFAULT,
  RUTA_REDES,
  conAdjunto,
  creditosDeRedes,
  alSumarVideoDeReferencia,
  bloqueoPorVideoDeReferencia,
  faltaEnRedes,
  huellaDeMejora,
  promptYaEscrito,
  rutaDeSesion,
  tituloDeSesion,
  type AjustesDeRedes,
  type HuellaDeMejora,
} from './redes';

const PANTALLA = 'el modo Redes';
/**
 * La pastilla "Ayuda de prompt" (abre el Agente en el panel con el prompt a medias) está
 * OCULTA: con "Mejorar prompt" en la caja, dos botones para lo mismo confundían
 * (ADR-2026-10-04-se-oculta-ayuda-de-prompt). El camino queda entero —la pantalla sigue
 * registrada en el puente y el Agente la lee y la escribe si se lo abre desde la sidebar—;
 * para volver a mostrarla alcanza con este `true`.
 */
const MOSTRAR_AYUDA_DE_PROMPT = false;
/**
 * Las plantillas dentro de Crear —la sección "Explora las plantillas" debajo de la caja y
 * el rótulo "Plantillas" del botón de los resultados, que queda como chevron— están OCULTAS: por ahora las plantillas se
 * prueban sólo como su propio modo, Recrear, que es lo que creemos que un usuario nuevo
 * tiene que ver primero (ADR-2026-10-05-crear-sin-plantillas). Todo sigue armado; para
 * volver a mostrarlas alcanza con este `true`. Sin la sección, en Video no hay dónde anclar
 * un tipo: queda en "general" y el escritor elige el que mejor calce.
 */
const MOSTRAR_PLANTILLAS = false;
/**
 * Lo que se le pide al escritor para la escena de Imagen automática: el producto va por las
 * fotos; esto le dice qué armar (ADR-2026-10-12-automatico-muestra-su-prompt-en-la-caja).
 */
const SEMILLA_DE_AUTOMATICO =
  'Una foto publicitaria para redes con este producto como protagonista: elegí la escena, la luz y la composición que mejor lo muestren.';
/** Los botones de Crear en el encabezado del panel del Agente: grises, como su chrome. */
const BOTON_DEL_PANEL =
  'inline-flex h-9 shrink-0 items-center gap-1.5 rounded-full bg-neutral-100 px-3 text-[13px] font-medium text-[#02062BE6] transition-colors hover:bg-neutral-200';
const BOTON_ESQUINA =
  'inline-flex h-10 shrink-0 items-center gap-2 rounded-full bg-white px-4 text-[13.5px] font-semibold text-brand-black shadow-[0_10px_30px_-10px_rgba(2,6,43,0.28)] ring-1 ring-black/[0.05] transition-colors hover:bg-[#F3F4F8]';

/** Una tanda pedida que todavía no se colgó: espera a que el chat de la sesión esté cargado. */
type TandaEnEspera = TandaPedida & { titulo: string };

export const PantallaRedes: React.FC<{ sesionId?: string }> = ({ sesionId }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const chat = useChatAtlas();
  const { abrirChat, fijarContexto } = useChatAtlasCascaron();
  const { companyId, userId, brandId } = useAgentEnv();
  const userModels = useUserModels();
  const { setSidebarMinima, pedirColapsarSidebar, sidebarMinima: tiraDeSidebar, setMobileHideBack } = useSidebar();
  // En el teléfono, el "Volver" del header se apaga: Crear trae el suyo, la pastilla con
  // el logo como la tira de escritorio.
  useEffect(() => {
    setMobileHideBack(true);
    return () => setMobileHideBack(false);
  }, [setMobileHideBack]);
  const acceso = useAccesoAlAgente();
  const { isDevTester } = useUserGroup();
  const esEscritorio = useEsEscritorio();
  // Lo que tapa el teclado en el teléfono: la caja sube eso (ADR-2026-10-14-la-caja-de-crear-sube-con-el-teclado).
  const tecladoAlto = useAltoDelTeclado(!esEscritorio);
  // Las fotos y los clips de la portada (abanicos y demo) siguen al rubro de la marca
  // (ADR-2026-10-12-la-portada-de-crear-sigue-al-rubro).
  const { rubro: rubroDeLaMarca } = useRubroMarcaActiva();
  const rubroDeLaPortada = rubroDeCrear(rubroDeLaMarca);

  // ── La caja ─────────────────────────────────────────────────────────────
  const [prompt, setPrompt] = useState('');
  // Una sugerencia del Asistente completa la caja y le da el foco; no manda
  // (ADR-2026-10-20-las-sugerencias-del-turno-completan-la-caja).
  const [pedidoDeFoco, setPedidoDeFoco] = useState(0);
  const completarConSugerencia = useCallback((texto: string) => {
    setPrompt(texto);
    setPedidoDeFoco((n) => n + 1);
  }, []);
  const [adjuntos, setAdjuntos] = useState<ChatAttachment[]>([]);
  const [ajustes, setAjustes] = useState<AjustesDeRedes>(AJUSTES_DEFAULT);
  const [fuentes, setFuentes] = useState<IdDeFuente | null>(null);
  const [generando, setGenerando] = useState(false);
  /** "Mejorar prompt": si está corriendo, y lo último que devolvió el escritor con su
   *  contexto, para no escribir dos veces lo mismo (ADR-2026-10-04-mejorar-prompt-en-redes). */
  const [mejorando, setMejorando] = useState(false);
  const [mejora, setMejora] = useState<HuellaDeMejora | null>(null);
  /** Cómo terminó la última mejora, un momento: el botón dibuja el tilde o la cruz. */
  const [resultadoDeMejora, setResultadoDeMejora] = useState<'listo' | 'fallo' | null>(null);
  useEffect(() => {
    if (!resultadoDeMejora) return;
    const t = window.setTimeout(() => setResultadoDeMejora(null), 1600);
    return () => window.clearTimeout(t);
  }, [resultadoDeMejora]);
  const [error, setError] = useState<string | null>(null);
  // ── El Asistente (ADR-2026-10-09-asistente-en-crear) ───────────────────
  /** La caja en Asistente: lo que se escribe va al Agente, no genera. */
  const [asistente, setAsistente] = useState(false);
  /** El prompt de la caja tiene el foco (la demo se achica mientras el usuario escribe). */
  const [escribiendo, setEscribiendo] = useState(false);
  const blurRef = useRef<number | null>(null);
  useEffect(() => () => {
    if (blurRef.current) window.clearTimeout(blurRef.current);
  }, []);
  /** Ya le mandó algo a Cumbre desde acá: la demo queda chica y dice dónde sigue. */
  const [conversando, setConversando] = useState(false);
  /** El pedido que espera a que el panel del chat esté abierto para salir. */
  const [pedidoAlAsistente, setPedidoAlAsistente] = useState<{ texto: string; adjuntos: ChatAttachment[] } | null>(null);
  /** SÓLO DEV: el motor de imagen forzado desde la pastilla DEV de la caja. No se guarda:
   *  al recargar vuelve a "Atlas elige" (ADR-2026-10-05-forzar-el-modelo-en-redes). */
  const [proveedorForzado, setProveedorForzado] = useState<ProveedorDeImagen | null>(null);
  const [limite, setLimite] = useState<TipoDeLimite | null>(null);
  const [lockVideo, setLockVideo] = useState<ModePlanLock | null>(null);
  /**
   * Qué se ve arriba de la caja: las plantillas (la portada, o "Plantillas" en una
   * sesión) o el lienzo. Es estado propio y no se deriva de la URL para poder animar el
   * cambio antes de navegar a la sesión nueva.
   */
  // Una sesión existente abre en sus creaciones, salvo que haga un rato que no se genera:
  // entonces en la portada (ADR-2026-10-08-redes-abre-colapsado-tras-un-rato). Se decide
  // ya en el primer render para que no se vea la cuadrícula y después salte.
  const [vista, setVista] = useState<'plantillas' | 'lienzo'>(() => {
    if (!sesionId) return 'plantillas';
    const nav = location.state as { verCreaciones?: unknown; conversacion?: unknown } | null;
    // Desde el Historial del Asistente se va a la CONVERSACIÓN, no a las creaciones
    // (ADR-2026-10-17-el-historial-del-asistente-abre-conversaciones).
    if (nav?.conversacion === true) return 'plantillas';
    return nav?.verCreaciones === true ? 'lienzo' : vistaAlEntrar();
  });
  const cambiarVista = useCallback((v: 'plantillas' | 'lienzo') => conTransicion(() => setVista(v)), []);
  const [titulo, setTitulo] = useState<string>('');

  const listo = !!userId && !!companyId;
  const creditos = creditosDeRedes(ajustes, adjuntos);
  // Además de lo de siempre, un video de referencia que no entra en la calidad elegida
  // (se bajó a Básica después de sumarlo) apaga Generar y dice por qué.
  const falta =
    faltaEnRedes({
      ajustes,
      prompt,
      listo,
      cantidadAdjuntos: adjuntos.length,
      cantidadProductos: adjuntos.filter((a) => a.kind === 'product').length,
      cantidadImagenes: adjuntos.filter((a) => a.kind === 'image').length,
    }) ??
    (esAnimar(ajustes) ? undefined : bloqueoPorVideoDeReferencia(ajustes, adjuntos));

  // Animar imagen (ADR-2026-10-09-modos-adentro-de-crear): elegida la foto y con la caja
  // vacía, el texto se completa solo con la idea de movimiento que sugiere el backend (la
  // misma de `AnimarImagenMode`, sin cobrar). Una vez por foto: si el usuario lo borra, no
  // vuelve a escribirse para esa misma foto.
  const fuenteAnimar = esAnimar(ajustes) ? fuenteDeAnimar(adjuntos) : null;
  const claveFuenteAnimar = fuenteAnimar && fuenteAnimar.tipo === 'subida' ? fuenteAnimar.path : null;
  const sugeridoParaRef = useRef<string | null>(null);
  const [sugiriendo, setSugiriendo] = useState(false);
  useEffect(() => {
    if (!fuenteAnimar || !claveFuenteAnimar || !userId) return;
    if (sugeridoParaRef.current === claveFuenteAnimar || prompt.trim()) return;
    sugeridoParaRef.current = claveFuenteAnimar;
    let cancelado = false;
    setSugiriendo(true);
    fetchSuggestedVideoPromptForSource(fuenteAnimar, userId)
      .then((sugerido) => {
        if (!cancelado && sugerido) setPrompt((actual) => (actual.trim() ? actual : sugerido));
      })
      .catch(() => {
        /* sin sugerencia el usuario escribe el movimiento */
      })
      .finally(() => {
        if (!cancelado) setSugiriendo(false);
      });
    return () => {
      cancelado = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [claveFuenteAnimar, userId]);

  // Animar con un producto y sin foto (ADR-2026-10-12-animar-usa-la-foto-del-producto): la
  // foto del producto se sube como referencia —una copia en el bucket de las subidas, el
  // único que el backend acepta como primer cuadro— y entra en "Añadir referencia", como si
  // el usuario la hubiera subido. De ahí sale la idea de movimiento (el efecto de arriba).
  // Una vez por producto: si el usuario la quita, no vuelve.
  const productoParaAnimar =
    esAnimar(ajustes) && !adjuntos.some((a) => a.kind === 'image') ? adjuntos.find((a) => a.kind === 'product') ?? null : null;
  const claveProductoParaAnimar = productoParaAnimar && productoParaAnimar.kind === 'product' ? productoParaAnimar.id : null;
  const animadoDesdeRef = useRef<string | null>(null);
  useEffect(() => {
    if (!productoParaAnimar || productoParaAnimar.kind !== 'product' || !claveProductoParaAnimar || !companyId) return;
    if (animadoDesdeRef.current === claveProductoParaAnimar || !productoParaAnimar.imageUrl) return;
    animadoDesdeRef.current = claveProductoParaAnimar;
    const url = productoParaAnimar.imageUrl;
    let cancelado = false;
    setSubiendo(true);
    void (async () => {
      try {
        const res = await fetch(url);
        if (!res.ok) throw new Error('No se pudo leer la foto del producto.');
        const blob = await res.blob();
        const ext = (blob.type.split('/')[1] || 'jpg').replace('jpeg', 'jpg');
        const archivo = new File([blob], `producto.${ext}`, { type: blob.type || 'image/jpeg' });
        const { storagePath, previewUrl } = await uploadReferenceImage(archivo, companyId);
        if (cancelado || !vivoRef.current) return;
        alAdjuntar({ kind: 'image', id: `img-${crypto.randomUUID()}`, storagePath, previewUrl });
      } catch {
        // Sin la copia, el usuario elige la foto en "Añadir referencia", como antes.
      } finally {
        // Aunque se haya cancelado: si no, "Subiendo…" quedaba prendido.
        if (vivoRef.current) setSubiendo(false);
      }
    })();
    return () => {
      cancelado = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [claveProductoParaAnimar, companyId]);

  // Imagen automática (ADR-2026-10-12-automatico-muestra-su-prompt-en-la-caja): con el
  // producto elegido y la caja vacía, el escritor de "Mejorar prompt" (las reglas del agente,
  // sin cobrar) arma una escena a partir de las fotos del producto y la deja en la caja, para
  // que el usuario la siga escribiendo o la cambie. Al generar, ese texto viaja al director de
  // arte como indicación, como cualquier texto en Automático. Una vez por juego de productos:
  // si el usuario lo borra, no vuelve a escribirse.
  const productosDeAuto = esAutomatico(ajustes) ? adjuntos.filter((a) => a.kind === 'product') : [];
  const claveAuto = productosDeAuto.length > 0 ? productosDeAuto.map((a) => a.id).sort().join('|') : null;
  const escritoParaRef = useRef<string | null>(null);
  useEffect(() => {
    if (!claveAuto || !userId || !companyId) return;
    if (escritoParaRef.current === claveAuto || prompt.trim()) return;
    escritoParaRef.current = claveAuto;
    let cancelado = false;
    setSugiriendo(true);
    escribirPromptEnRedes({
      userId,
      companyId,
      prompt: SEMILLA_DE_AUTOMATICO,
      adjuntos: productosDeAuto,
      ajustes,
      modeloUrl: () => null,
    })
      .then((escrito) => {
        if (!cancelado && escrito.prompt) setPrompt((actual) => (actual.trim() ? actual : escrito.prompt));
      })
      .catch(() => {
        /* sin escena escrita, el director la arma al generar, como siempre */
      })
      .finally(() => {
        if (!cancelado) setSugiriendo(false);
      });
    return () => {
      cancelado = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [claveAuto, userId, companyId]);

  const vivoRef = useRef(true);
  useEffect(() => {
    vivoRef.current = true;
    return () => {
      vivoRef.current = false;
    };
  }, []);

  // ── La sesión es un chat (como un diseño) ───────────────────────────────
  const chatActual = chat.motor.getChatId();
  const esElChat = !!sesionId && chatActual === sesionId;
  const cargarChat = chat.cargarChat;
  useEffect(() => {
    if (!sesionId || chat.motor.getChatId() === sesionId) return;
    void cargarChat(sesionId)
      .then(() => {
        if (chat.motor.getChatId() !== sesionId) {
          navigate(RUTA_REDES, { replace: true, state: { aviso: 'Esa sesión no existe o no es de tu empresa.' } });
        }
      })
      .catch(() => {
        navigate(RUTA_REDES, { replace: true, state: { aviso: 'No se pudo abrir la sesión. Intentá de nuevo.' } });
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sesionId]);

  useEffect(() => {
    if (!sesionId) {
      setVista('plantillas');
      return;
    }
    // Una sesión recién creada (la primera, o "Nueva sesión") abre en las plantillas: no
    // hay nada en el lienzo todavía. Una existente abre en el lienzo, salvo que haga un
    // rato que no se genera (entonces en la portada, con las creaciones colapsadas). Pedir
    // las creaciones explícitamente ("Volver a creaciones" del Estudio) gana siempre.
    const estadoNav = location.state as { nueva?: unknown; irAPlantillas?: unknown; verCreaciones?: unknown; desdeCero?: unknown; conversacion?: unknown } | null;
    const irAPlantillas = estadoNav?.irAPlantillas === true;
    // Entrando por "Crear desde cero", la caja arranca en Imagen, sin modo y fuera del
    // Asistente, aunque la pantalla viniera de otra cosa (ADR-2026-10-13-crear-desde-cero-abre-un-chat-nuevo).
    // Si la entrada pidió un medio o un modo (Creador de video, Imagen automática), lo aplican
    // sus efectos de siempre.
    if (estadoNav?.desdeCero === true) {
      setAsistente(false);
      // El estado de navegación sobrevive a recargar: recargando una sesión que ya charló con
      // el Asistente, `desdeCero` vuelve a pasar por acá. Soltar la marca deja que el efecto de
      // más abajo la reabra en el Asistente, con su conversación a la vista; si no, quedaba
      // escondida detrás de Imagen (ADR-2026-10-16-un-solo-historial-agente-y-asistente).
      sesionAbiertaEnAsistenteRef.current = null;
      if (!medioDeEntrada(location.state) && !modoDeEntrada(location.state)) {
        setAjustes((a) => conModo({ ...a, medio: AJUSTES_DEFAULT.medio }, undefined));
      }
    }
    setVista(
      // `conversacion`: elegida en el Historial del Asistente, abre en la conversación aunque
      // se haya generado hace poco (ADR-2026-10-17-el-historial-del-asistente-abre-conversaciones).
      estadoNav?.nueva || irAPlantillas || estadoNav?.conversacion === true
        ? 'plantillas'
        : estadoNav?.verCreaciones === true
          ? 'lienzo'
          : vistaAlEntrar(),
    );
    // Volviendo de una plantilla: la vista de plantillas, scrolleada a la sección.
    if (irAPlantillas) {
      window.setTimeout(() => {
        document.getElementById('plantillas-de-redes')?.scrollIntoView({ block: 'start' });
      }, 60);
    }
    void leerTituloDeChat(sesionId).then((t) => {
      if (vivoRef.current && t) setTitulo(t);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sesionId]);
  // El título que la IA le pone a la sesión al terminar la primera respuesta del Asistente
  // (ADR-2026-10-13-el-titulo-del-chat-lo-escribe-la-ia): reemplaza al provisorio.
  useEffect(() => {
    if (!sesionId) return;
    const alTitular = (e: Event) => {
      const { chatId, titulo: nuevo } = (e as CustomEvent<DetalleChatTitulado>).detail;
      if (chatId === sesionId) setTitulo(nuevo);
    };
    window.addEventListener(EVENTO_CHAT_TITULADO, alTitular);
    return () => window.removeEventListener(EVENTO_CHAT_TITULADO, alTitular);
  }, [sesionId]);

  // El medio con el que mandaron a abrir: Creador de video → Video, Prompt libre → Imagen
  // (ADR-2026-10-07-prompt-libre-y-creador-abren-crear). Sólo en una sesión: en `/redes`
  // a secas el `replace` hacia la sesión lo arrastra (más abajo) y se aplica acá al llegar.
  // Se aplica y se saca del estado de navegación para que recargar no lo vuelva a poner.
  // El MODO de Crear llega igual: la card "Imagen automática" del popup de Redes sociales abre
  // Crear con ese modo elegido, como tocarlo en la tarjeta Modo de la caja (conModo + el
  // selector de productos si todavía no hay uno) — ADR-2026-10-12-imagen-automatica-en-el-popup-de-redes.
  useEffect(() => {
    if (!sesionId) return;
    const medio = medioDeEntrada(location.state);
    const modo = modoDeEntrada(location.state);
    if (!medio && !modo) return;
    if (medio) setAjustes((a) => (a.medio === medio ? a : { ...a, medio }));
    if (modo) {
      setAjustes((a) => conModo(a, modo));
      if (modo === 'automatico' && !adjuntos.some((a) => a.kind === 'product')) setFuentes('productos');
      if (modo === 'animar' && !adjuntos.some((a) => a.kind === 'image')) setFuentes('subidas');
    }
    navigate(location.pathname, { replace: true, state: estadoSinMedio(location.state) });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key, sesionId]);

  // El aviso con el que la sesión mandó de vuelta a la portada.
  useEffect(() => {
    const aviso = (location.state as { aviso?: unknown } | null)?.aviso;
    if (typeof aviso !== 'string') return;
    setError(aviso);
    navigate(location.pathname, { replace: true, state: null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Siempre la misma sesión ──────────────────────────────────────────────
  // `/redes` no es una portada con lista: resuelve a la ÚLTIMA sesión de la empresa y, si
  // no hay ninguna, crea la primera. "Nueva sesión" (el botón de la esquina) es la única
  // forma de abrir otra. Una sesión nueva nace con un título de fecha y toma el prompt de
  // la primera generación como nombre (ver `handleGenerar`).
  const creandoSesionRef = useRef(false);
  const crearSesion = useCallback(async (): Promise<string | null> => {
    if (!userId || !companyId || creandoSesionRef.current) return null;
    creandoSesionRef.current = true;
    try {
      const fecha = new Date().toLocaleDateString('es-AR', { day: 'numeric', month: 'short' });
      return await createChat({ userId, companyId, title: `Sesión ${fecha}`, messages: [], tipo: TIPO_CHAT_REDES });
    } finally {
      creandoSesionRef.current = false;
    }
  }, [userId, companyId]);
  useEffect(() => {
    if (sesionId || !listo) return;
    let vivo = true;
    void (async () => {
      const reusable = userId ? await sesionDeRedesVaciaParaReusar(userId) : null;
      if (!vivo) return;
      const irAPlantillas = (location.state as { irAPlantillas?: unknown } | null)?.irAPlantillas === true;
      // El medio pedido (Creador de video → Video) viaja a la sesión con el `replace`,
      // como `irAPlantillas` (ADR-2026-10-07-prompt-libre-y-creador-abren-crear).
      const medio = medioDeEntrada(location.state);
      // Y el modo de Crear (la card "Imagen automática"), igual que el medio.
      const modo = modoDeEntrada(location.state);
      const pedido = { ...(medio ? { medio } : {}), ...(modo ? { modo } : {}) };
      // Entrar a Crear ("Crear desde cero") es empezar un chat NUEVO, en Imagen
      // (ADR-2026-10-13-crear-desde-cero-abre-un-chat-nuevo): se reusa la sesión vacía más
      // nueva del usuario (sin mensajes ni nada en el lienzo), para no juntar sesiones vacías
      // (ADR-2026-10-16-un-solo-historial-agente-y-asistente). A las anteriores se vuelve por
      // su enlace o desde el Historial.
      const estado = { ...(irAPlantillas ? { irAPlantillas: true } : {}), nueva: true, desdeCero: true, ...pedido };
      if (reusable) {
        navigate(rutaDeSesion(reusable), { replace: true, state: estado });
        return;
      }
      const id = await crearSesion();
      if (!vivo) return;
      if (id) navigate(rutaDeSesion(id), { replace: true, state: { nueva: true, desdeCero: true, ...pedido } });
      else setError('No se pudo abrir una sesión. Recargá la página.');
    })();
    return () => {
      vivo = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sesionId, listo]);
  const nuevaSesion = useCallback(async () => {
    const id = await crearSesion();
    if (!vivoRef.current) return;
    if (id) {
      setTitulo('');
      // La sesión nueva arranca sin conversación: la demo vuelve a contar qué hace Cumbre y
      // el panel del Asistente (si estaba abierto sobre el Canva) se cierra.
      setConversando(false);
      if (chat.abierto && chat.vista === 'panel') chat.cerrarChat();
      navigate(rutaDeSesion(id), { state: { nueva: true } });
    } else setError('No se pudo crear la sesión.');
  }, [crearSesion, navigate, chat]);

  // La sidebar a la tira desde que se entra (portada y sesión), como el Agente.
  useEffect(() => {
    setSidebarMinima(true);
    pedirColapsarSidebar();
    return () => setSidebarMinima(false);
  }, [setSidebarMinima, pedirColapsarSidebar]);

  // El panel del agente se va con la sesión: si quedó abierto sobre ESTE chat, se cierra
  // al salir (misma regla que `hayQueCerrarElPanelAlSalir` de Diseño).
  const alSalirRef = useRef<() => void>(() => {});
  alSalirRef.current = () => {
    if (sesionId && chat.abierto && chat.vista === 'panel' && chat.motor.getChatId() === sesionId) chat.cerrarChat();
  };
  useEffect(() => () => alSalirRef.current(), []);

  // ── Los resultados: la grilla del Creador o el canva ─────────────────────
  // La MISMA grilla que el Creador de video (`PanelTandaCreaciones` con el registro de
  // tandas en curso): todas las imágenes y videos de la empresa, con los recuadros de lo
  // que está generando arriba. "Canva" es el lienzo del Agente. Una tanda se registra en
  // los DOS lados con la misma promesa; cada vista dibuja la suya.
  const { tandasPanel, recargarToken, crearTanda, cerrarTanda, fallarTanda } = useTandasPanel(companyId, 'redes');
  const [modoDeVista, setModoDeVista] = useState<'cuadricula' | 'canva'>('cuadricula');
  // En el teléfono no hay Canva: siempre la cuadrícula (ADR-2026-10-09-asistente-en-crear).
  // En el Asistente, sólo Canva: lo que arma Cumbre se ve en su lienzo
  // (ADR-2026-10-12-la-conversacion-del-asistente-en-crear).
  const vistaDeResultados = !esEscritorio ? 'cuadricula' : asistente ? 'canva' : modoDeVista;
  // El último proyecto del Estudio abierto desde esta sesión: con él, al lado de
  // Cuadrícula | Canva aparece "Estudio de video" (ADR-2026-10-05-el-estudio-desde-redes-queda-a-mano).
  // Se lee al montar: volver del editor vuelve a montar la pantalla.
  const proyectoDelEstudio = useMemo(() => proyectoDeRedes(sesionId), [sesionId]);
  const registrarEnPanel = useCallback(
    (t: { total: number; ratio: number; esVideo: boolean; executionId?: string; duracionEstimadaMs?: number; resultado: Promise<string[]> }) => {
      if (!companyId) return;
      // Se generó en Redes (desde la caja o desde el visor): cuenta para abrir en las
      // creaciones la próxima vez (ADR-2026-10-08-redes-abre-colapsado-tras-un-rato).
      recordarGeneracionEnRedes();
      const id = crearTanda({
        total: t.total,
        ratio: t.ratio,
        modo: 'redes',
        esVideo: t.esVideo,
        ...(t.executionId ? { executionIds: [t.executionId] } : {}),
        ...(t.duracionEstimadaMs ? { duracionEstimadaMs: t.duracionEstimadaMs } : {}),
      });
      if (!id) return;
      t.resultado
        .then(async (ids) => {
          let pre: PreloadedImage[] = [];
          try {
            pre = t.esVideo ? await preloadVideosDeTanda(ids, companyId) : await preloadGeneratedImages(ids, companyId);
          } catch {
            /* sin preload cierra vacía y la grilla se recarga */
          }
          cerrarTanda(id, pre, { esVideo: t.esVideo });
          sonarListo();
        })
        .catch(() => fallarTanda(id));
    },
    [companyId, crearTanda, cerrarTanda, fallarTanda],
  );
  // Carrusel / Animar pedidos desde el visor de la grilla aterrizan como una tanda más.
  const alDelegar = useCallback(
    (g: GeneracionDelegada) => {
      registrarEnPanel({ total: g.total, ratio: g.ratio, esVideo: g.esVideo, executionId: g.executionIds?.[0], duracionEstimadaMs: g.duracionEstimadaMs, resultado: g.resultado });
    },
    [registrarEnPanel],
  );
  // Cuánto mide la caja que flota abajo, para que la grilla pueda scrollear por debajo
  // hasta el final (como `altoCaja` en el Creador).
  const cajaRef = useRef<HTMLDivElement | null>(null);
  const [altoCaja, setAltoCaja] = useState(260);
  useEffect(() => {
    const nodo = cajaRef.current;
    if (!nodo || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(([e]) => {
      const alto = e?.borderBoxSize?.[0]?.blockSize ?? nodo.offsetHeight;
      if (alto > 0) setAltoCaja(alto);
    });
    ro.observe(nodo);
    return () => ro.disconnect();
  }, [sesionId, vista]);

  // ── El lienzo ───────────────────────────────────────────────────────────
  const estado = useLienzoDelChat();
  const contenedorRef = useRef<HTMLDivElement | null>(null);
  const [ancho, setAncho] = useState(0);
  const lienzoVisible = !!sesionId && vista === 'lienzo';
  useEffect(() => {
    const el = contenedorRef.current;
    if (!el || !lienzoVisible || typeof ResizeObserver === 'undefined') return;
    const medir = () => setAncho(el.clientWidth);
    medir();
    const ro = new ResizeObserver(medir);
    ro.observe(el);
    return () => ro.disconnect();
  }, [lienzoVisible]);

  const colgar = useCallback(
    (t: TandaEnEspera) => {
      registrarEnPanel(t);
      estado.agregarTanda({
        titulo: t.titulo,
        total: t.total,
        ratio: t.ratio,
        esVideo: t.esVideo,
        executionId: t.executionId,
        resultado: t.resultado,
      });
      t.resultado.catch((e: unknown) => {
        if (vivoRef.current) setError(e instanceof Error && e.message ? e.message : 'No se pudo generar. Intentá nuevamente.');
      });
    },
    [estado, registrarEnPanel],
  );

  // Una tanda pedida desde la portada espera a que el chat de la sesión nueva esté cargado.
  const enEsperaRef = useRef<TandaEnEspera | null>(null);
  useEffect(() => {
    if (!esElChat || !enEsperaRef.current) return;
    const t = enEsperaRef.current;
    enEsperaRef.current = null;
    colgar(t);
  }, [esElChat, colgar]);

  // ── Generar ─────────────────────────────────────────────────────────────
  const handleGenerar = async () => {
    if (falta || generando || !userId || !companyId) return;
    setGenerando(true);
    try {
      // Video pide Pro/Ultra, el mismo piso del Creador. El plan se vuelve a leer acá
      // porque entrar al modo no lo pide.
      if (ajustes.medio === 'video' && !isDevTester) {
        const lock = getModePlanLock('creador-video', await getUserPlan());
        if (lock) {
          setLockVideo(lock);
          return;
        }
      }
      const saldo = await consultarSaldo(companyId);
      if (!vivoRef.current) return;
      if (saldo.restantes !== null && saldo.restantes < creditos) {
        setLimite(tipoDeLimite(saldo.plan));
        return;
      }
      const pedido = pedirEnRedes({
        userId,
        companyId,
        prompt,
        adjuntos,
        ajustes,
        modeloUrl: (id) => userModels.find((m) => String(m.id) === id)?.thumbnailUrl ?? null,
        // Si el prompt es el que devolvió "Mejorar prompt" (igual o casi, mismo contexto),
        // el Creador no lo vuelve a escribir.
        promptEscrito: promptYaEscrito(mejora, prompt, ajustes, adjuntos),
        // Imagen: lo que la mejora ya declaró (motor, fondo blanco, etiquetas) viaja con
        // el prompt, así no se decide otra vez.
        declarado: promptYaEscrito(mejora, prompt, ajustes, adjuntos) ? mejora?.declarado : undefined,
        // Sólo cuentas dev: el motor forzado desde la pastilla DEV.
        ...(isDevTester && proveedorForzado ? { proveedorForzado } : {}),
      });
      const tanda: TandaEnEspera = { ...pedido, titulo: tituloDeSesion(prompt, ajustes.medio) };
      cambiarVista('lienzo');
      if (sesionId) {
        // La primera generación le pone nombre a la sesión (nació con la fecha). Sólo si sigue
        // con ese nombre: desde el Historial también se abren acá chats del Agente, que ya
        // tienen el suyo (ADR-2026-10-16-un-solo-historial-agente-y-asistente).
        if (estado.tandas.length === 0 && (!titulo || titulo.startsWith('Sesión'))) {
          void renombrarChat(sesionId, tanda.titulo).then((ok) => {
            if (ok && vivoRef.current) setTitulo(tanda.titulo);
          });
        }
        if (esElChat) colgar(tanda);
        else enEsperaRef.current = tanda;
        return;
      }
      // Sin sesión: se crea y se navega. La tanda ya está en vuelo; se cuelga apenas el
      // chat nuevo sea el actual (`enEsperaRef`).
      enEsperaRef.current = tanda;
      const id = await createChat({ userId, companyId, title: tanda.titulo, messages: [], tipo: TIPO_CHAT_REDES });
      if (!vivoRef.current) return;
      if (!id) {
        enEsperaRef.current = null;
        setError('No se pudo crear la sesión. El pedido igual salió: lo vas a encontrar en Creaciones.');
        return;
      }
      setTitulo(tanda.titulo);
      navigate(rutaDeSesion(id));
    } finally {
      if (vivoRef.current) setGenerando(false);
    }
  };

  // ── Adjuntos (el selector del Agente, en la pestaña que corresponda) ────
  const alAdjuntar = useCallback((a: ChatAttachment) => setAdjuntos((prev) => conAdjunto(prev, a)), []);
  const quitarAdjunto = useCallback((id: string) => setAdjuntos((prev) => prev.filter((a) => a.id !== id)), []);

  // ── Pegar (Ctrl/Cmd+V) o soltar una imagen: entra como referencia, igual que subirla
  //    desde "Añadir referencia" (ADR-2026-10-04-pegar-imagenes-en-la-caja-de-redes). El
  //    mismo hook de Prompt libre y el Creador, acotado a esta pantalla: el panel del
  //    Agente tiene su propio pegado. Un pegado de texto no se toca.
  const raizRef = useRef<HTMLDivElement | null>(null);
  const [subiendo, setSubiendo] = useState(false);
  const subirImagenes = useCallback(
    async (archivos: File[]) => {
      if (!companyId || archivos.length === 0) return;
      setSubiendo(true);
      let fallo: string | null = null;
      // De a una y en orden: el "subiendo" de la caja refleja la que está en vuelo.
      for (const archivo of archivos) {
        try {
          const { storagePath, previewUrl } = await uploadReferenceImage(archivo, companyId);
          if (!vivoRef.current) return;
          alAdjuntar({ kind: 'image', id: `img-${crypto.randomUUID()}`, storagePath, previewUrl });
        } catch (e) {
          fallo = fallo ?? (e instanceof Error && e.message ? e.message : 'No se pudo subir esa imagen.');
        }
      }
      if (!vivoRef.current) return;
      setSubiendo(false);
      if (fallo) setError(fallo);
    },
    [companyId, alAdjuntar],
  );
  // ── Una creación pasa a ser referencia de la caja, volando ──────────────
  //    La foto se achica y vuela a la tarjeta "Añadir referencia"; al llegar entra como
  //    adjunto (ahí aparece su miniatura). Lo usan "Editar" de la cuadrícula (que además
  //    escribe el prompt, `alLlegar`) y "Agregar" del lienzo
  //    (ADR-2026-10-03-redes-editar-vuela-a-la-referencia,
  //    ADR-2026-10-04-agregar-del-lienzo-va-a-la-caja-de-redes). Sin origen o sin destino
  //    a la vista, entra directo.
  const volarAReferencia = useCallback(
    (adjunto: ChatAttachment, origen?: { rect: DOMRect; src: string }, alLlegar?: () => void) => {
      const destino = document.querySelector('[data-destino-referencia]');
      const sumar = () => {
        setAdjuntos((prev) => conAdjunto(prev, adjunto));
        alLlegar?.();
        latido(destino);
      };
      if (!origen || !destino) {
        sumar();
        return;
      }
      const r = destino.getBoundingClientRect();
      // Aterriza sobre el ícono de la tarjeta (42 px, a la izquierda).
      const blanco = new DOMRect(r.left + 12, r.top + (r.height - 42) / 2, 42, 42);
      void volarHacia(origen.src, origen.rect, blanco).then(sumar);
    },
    [],
  );
  // Un VIDEO como referencia tiene sus reglas (ADR-2026-10-05-bloqueos-del-video-de-referencia):
  // sólo sirve para generar video (la caja pasa a Video), es uno solo (`conAdjunto` reemplaza
  // al anterior) y su largo tiene tope según la calidad: si no entra en Básica se pasa a
  // Profesional avisando, y si no entra ni ahí no se suma. Devuelve si se sumó.
  const calidadDeVideoRef = useRef(ajustes.video.ajustes.quality);
  calidadDeVideoRef.current = ajustes.video.ajustes.quality;
  const sumarVideoDeReferencia = useCallback(
    async (crudo: ChatAttachment, origen?: { rect: DOMRect; src: string }) => {
      if (crudo.kind !== 'video') return;
      // Si la fila no decía cuánto dura (Animar imagen, plantillas, Corregir voz), se mide
      // del archivo: sin el dato no hay tope que cuidar.
      let adjunto = crudo;
      if (!(crudo.duracionSeg > 0)) {
        const url = await signReferenceImage(crudo.storagePath, crudo.bucket).catch(() => null);
        const medido = url ? await medirDuracionDeVideo(url) : 0;
        if (!vivoRef.current) return;
        if (medido > 0) adjunto = { ...crudo, duracionSeg: Math.round(medido * 10) / 10 };
      }
      const r = alSumarVideoDeReferencia(adjunto.duracionSeg, calidadDeVideoRef.current);
      if (r.accion === 'bloquear') {
        setError(r.motivo);
        return;
      }
      setAjustes((a) => ({
        ...a,
        medio: 'video',
        video: r.accion === 'subir' ? { ...a.video, ajustes: conCalidad(a.video.ajustes, 'profesional') } : a.video,
      }));
      if (r.accion === 'subir') setError(r.aviso);
      volarAReferencia(adjunto, origen);
    },
    [volarAReferencia],
  );
  // "Agregar" en el lienzo: la foto NO va al chat, va de referencia a la caja.
  const agregarDelLienzo = useMemo(
    () => ({
      rotulo: 'Agregar',
      agregar: (adjunto: ChatAttachment, origen?: { rect: DOMRect; src: string }) =>
        adjunto.kind === 'video' ? void sumarVideoDeReferencia(adjunto, origen) : volarAReferencia(adjunto, origen),
    }),
    [volarAReferencia, sumarVideoDeReferencia],
  );

  // ── Rehacer: lo que generó una creación vuelve a la caja ────────────────
  //    (ADR-2026-10-04-rehacer-desde-la-cuadricula). El prompt es el que el usuario mandó
  //    (no el del escritor), con sus adjuntos y los ajustes de ese medio. La huella de
  //    "Mejorar prompt" se borra: ese texto no salió de una mejora de ahora.
  const rehacer = useCallback(async (datosGeneracion: unknown) => {
    const receta = leerReceta(datosGeneracion);
    if (!receta) return;
    setAjustes((a) => ajustesConReceta(a, receta));
    setPrompt(receta.prompt);
    setMejora(null);
    setAdjuntos(receta.adjuntos);
    // Las imágenes subidas guardaron una vista previa FIRMADA, que vence: se vuelve a
    // firmar. La referencia que viaja al generar se firma aparte, por su path.
    const ids = receta.adjuntos.map((a) => a.id).join(',');
    const frescos = await Promise.all(
      receta.adjuntos.map(async (a) => {
        if (a.kind !== 'image') return a;
        try {
          const url = await signReferenceImage(a.storagePath, a.bucket);
          return url ? { ...a, previewUrl: url } : a;
        } catch {
          return a;
        }
      }),
    );
    if (!vivoRef.current) return;
    // Sólo si la caja sigue con esos mismos adjuntos: si el usuario ya tocó algo, manda él.
    setAdjuntos((prev) => (prev.map((a) => a.id).join(',') === ids ? frescos : prev));
  }, []);

  const { arrastrando } = usePegarYSoltarImagenes({
    // Con el selector de fuentes o un aviso abierto, la foto no tiene que caer atrás.
    activo: listo && fuentes === null && !limite && !lockVideo,
    onImagenes: (archivos) => void subirImagenes(archivos),
    dentroDe: raizRef,
  });

  // ── Mejorar prompt: el escritor del Creador, suelto, y su texto a la caja ──
  const mejorarPrompt = useCallback(async () => {
    if (mejorando || generando || !userId || !companyId) return;
    setMejorando(true);
    setResultadoDeMejora(null);
    try {
      const escrito = await escribirPromptEnRedes({
        userId,
        companyId,
        prompt,
        adjuntos,
        ajustes,
        modeloUrl: (id) => userModels.find((m) => String(m.id) === id)?.thumbnailUrl ?? null,
      });
      if (!vivoRef.current) return;
      setPrompt(escrito.prompt);
      setMejora(huellaDeMejora(escrito.prompt, ajustes, adjuntos, escrito.declarado));
      setResultadoDeMejora('listo');
    } catch (e) {
      if (vivoRef.current) {
        setResultadoDeMejora('fallo');
        setError(e instanceof Error && e.message ? e.message : 'No pudimos mejorar el prompt. Probá de nuevo.');
      }
    } finally {
      if (vivoRef.current) setMejorando(false);
    }
  }, [mejorando, generando, userId, companyId, ajustes, prompt, adjuntos, userModels]);

  // ── Ayuda de prompt: el agente en el panel, con el prompt a medias precargado ─
  const ayudaDePrompt = useCallback(() => {
    const medio = ajustes.medio === 'video' ? 'un video' : 'una imagen';
    const texto = prompt.trim();
    abrirChat({
      contexto: { pantalla: PANTALLA, ruta: location.pathname, detalle: titulo ? `la sesión «${titulo}»` : undefined },
      vista: 'panel',
      mensajeInicial: texto
        ? `Ayudame a mejorar este prompt para ${medio} para redes: «${texto}»`
        : `Ayudame a escribir el prompt para ${medio} para redes. Quiero `,
    });
  }, [abrirChat, ajustes.medio, prompt, location.pathname, titulo]);

  // ── El Asistente: el pedido va al Agente, en el chat de ESTA sesión ──────
  //    (ADR-2026-10-09-asistente-en-crear). La sesión de Crear ya es un chat: el mensaje
  //    sale por el MISMO motor del Agente, así el agente ve la pantalla por el puente
  //    (`describirRedes`) y lo que genera queda en la sesión. La conversación se ve acá, en
  //    el medio (`MensajesDeLaConversacion`), y no en el panel
  //    (ADR-2026-10-12-la-conversacion-del-asistente-en-crear): el contexto de apertura se
  //    fija sin abrir el chat. Los adjuntos de la caja viajan con el mensaje.
  const cerrarChatRef = useRef(chat.cerrarChat);
  cerrarChatRef.current = chat.cerrarChat;
  const contextoDeCrear = useMemo(
    () => ({
      pantalla: PANTALLA,
      ruta: location.pathname,
      detalle: titulo ? `la sesión «${titulo}»` : undefined,
      // El panel se presenta como "Asistente", la pestaña desde la que se habla
      // (ADR-2026-10-13-el-panel-del-asistente-se-llama-asistente).
      titulo: 'Asistente',
      // Y con las tarjetas de Producto, Avatar y Referencia arriba de su caja
      // (ADR-2026-10-13-las-tarjetas-de-crear-en-el-panel-del-asistente).
      conTarjetasDeReferencia: true,
      // Nuevo chat e Historial a la vista en el encabezado: los de CREAR (una sesión nueva y
      // las sesiones), no los del Agente, que crearían un chat fuera de Crear
      // (ADR-2026-10-13-nuevo-chat-e-historial-a-la-vista-en-el-asistente).
      // Agrandar despliega el panel en su lugar, sobre el lienzo, y lo vuelve a achicar
      // (ADR-2026-10-13-agrandar-el-asistente-despliega-el-panel).
      agrandarEnElLugar: true,
      // Agrandado, el panel tapa el "Volver" de Crear: lo repone en su encabezado y hace lo
      // mismo —la home—, cerrando antes el chat (ADR-2026-10-13-volver-en-el-asistente-agrandado).
      alVolver: () => {
        cerrarChatRef.current();
        navigate('/interfaz-v2');
      },
      accionesDelEncabezado: (
        <>
          <button type="button" onClick={() => void nuevaSesion()} className={BOTON_DEL_PANEL}>
            <Plus className="h-4 w-4" strokeWidth={2.25} aria-hidden="true" />
            Nuevo chat
          </button>
          {sesionId && (
            <HistorialDeSesiones
              actual={sesionId}
              soloConversaciones
              onElegir={(id) => navigate(rutaDeSesion(id), { state: { conversacion: true } })}
              claseDelBoton={BOTON_DEL_PANEL}
              className="relative"
            />
          )}
        </>
      ),
    }),
    [location.pathname, titulo, nuevaSesion, sesionId, navigate],
  );
  /** Sube con cada pedido: la conversación vuelve a seguir la respuesta. */
  const [pedidoDeIrAlFondo, setPedidoDeIrAlFondo] = useState(0);
  /** Se mandó un pedido desde acá y todavía no arrancó una imagen: al arrancar, Canva. */
  const esperaImagenRef = useRef(false);
  const enviarAlAsistente = useCallback(() => {
    const texto = prompt.trim();
    if (!texto || !userId || !companyId) return;
    fijarContexto(contextoDeCrear);
    esperaImagenRef.current = true;
    // El primer pedido le pone nombre a la sesión (nació "Sesión 13 oct"), como la primera
    // generación de la caja: así se reconoce en el Historial (ADR-2026-10-13-historial-en-el-asistente).
    if (sesionId && esElChat && !chat.mensajes.some((m) => m.role === 'user') && (!titulo || titulo.startsWith('Sesión'))) {
      const nombre = tituloDeSesion(texto, ajustes.medio);
      void renombrarChat(sesionId, nombre).then((ok) => {
        if (ok && vivoRef.current) setTitulo(nombre);
      });
    }
    // Con una transición de vista, como "Ver creaciones": la caja VIAJA del medio al pie
    // (`view-transition-name`) y la demo se funde con la conversación, en vez de saltar
    // (ADR-2026-10-12-la-conversacion-del-asistente-en-crear-2).
    conTransicion(() => {
      setPedidoAlAsistente({ texto, adjuntos });
      setPrompt('');
      setAdjuntos([]);
      setMejora(null);
      setConversando(true);
      setPedidoDeIrAlFondo((n) => n + 1);
    });
  }, [prompt, userId, companyId, fijarContexto, contextoDeCrear, adjuntos, sesionId, esElChat, chat.mensajes, titulo, ajustes.medio]);
  useEffect(() => {
    if (!pedidoAlAsistente || !esElChat) return;
    const { texto, adjuntos: delPedido } = pedidoAlAsistente;
    setPedidoAlAsistente(null);
    // `reemplazarAdjuntos` escribe también el ref que lee el envío, así salen en este mismo
    // tick (el `agregarAdjunto` de a uno espera al próximo render).
    if (delPedido.length > 0) chat.motor.reemplazarAdjuntos([...chat.adjuntos, ...delPedido]);
    if (chat.streaming) {
      if (!chat.encolarMensaje(texto)) setError('El Asistente todavía está respondiendo. Probá en un momento.');
    } else {
      chat.enviarMensaje(texto);
    }
  }, [pedidoAlAsistente, chat, esElChat]);
  // Cuando lo que se pidió empieza a generarse (aparecen los recuadros de la primera
  // imagen), la pantalla pasa al Canva y la conversación sigue en el panel del Agente, el
  // mismo reparto que la pantalla del Agente con su lienzo. Sólo en la computadora: en el
  // teléfono no hay Canva y lo generado se ve en la conversación.
  const generandoEnElChat = useMemo(() => hayGeneracionEnVuelo(chat.mensajes), [chat.mensajes]);
  // El cambio entero (Canva + panel) en UNA transición de vista, más lenta y suave que la de
  // fábrica (`data-transicion` en `crear.css`): la conversación se funde y llega el lienzo con
  // el panel ya abierto (ADR-2026-10-13-el-asistente-se-apaga-hacia-el-lienzo).
  const abrirCanvaDelAsistente = useCallback(() => {
    const raiz = document.documentElement;
    raiz.dataset.transicion = 'asistente-canva';
    conTransicion(() => {
      setModoDeVista('canva');
      setVista('lienzo');
      abrirChat({ contexto: contextoDeCrear, vista: 'panel' });
    });
    window.setTimeout(() => {
      if (raiz.dataset.transicion === 'asistente-canva') delete raiz.dataset.transicion;
    }, 1200);
  }, [abrirChat, contextoDeCrear]);
  useEffect(() => {
    if (!asistente || !esEscritorio || !esElChat || !generandoEnElChat || !esperaImagenRef.current) return;
    esperaImagenRef.current = false;
    abrirCanvaDelAsistente();
  }, [asistente, esEscritorio, esElChat, generandoEnElChat, abrirCanvaDelAsistente]);
  // Salir del Canva del Asistente (a la portada, o a Imagen / Video): el panel se cierra y
  // la conversación vuelve al medio, así no se ve dos veces.
  const cerrarPanelDelAsistente = useCallback(() => {
    if (chat.abierto && chat.vista === 'panel' && esElChat) chat.cerrarChat();
  }, [chat, esElChat]);

  // ── Plantillas de imagen: el flujo de plantilla de siempre ──────────────
  const elegirPlantilla = useCallback(
    (p: PlantillaElegida) => {
      navigate(`/interfaz-v2?preset=${p.preset.id}`, {
        state: {
          presetId: p.preset.id,
          collectionId: p.collectionId,
          presetData: p.preset,
          collectionPresets: p.collectionPresets,
          saltearPreview: true,
          // Las migas de la plantilla vuelven acá ("Plantillas" → Redes, en su sección).
          desdeRedes: true,
        },
      });
    },
    [navigate],
  );

  // ── El puente con el agente ─────────────────────────────────────────────
  const nombresDeAdjuntos = useMemo(
    () => adjuntos.map((a) => (a.kind === 'image' ? 'una imagen subida' : `${a.kind === 'product' ? 'Producto' : a.kind === 'model' ? 'Modelo' : 'Referencia'}: ${a.name}`)),
    [adjuntos],
  );
  useRegistrarPantalla(
    useMemo<ControladorDePantalla>(
      () => ({
        clave: CLAVE_PANTALLA_REDES,
        describir: () =>
          describirRedes({
            ruta: location.pathname,
            prompt,
            ajustes,
            cantidadAdjuntos: adjuntos.length,
            cantidadProductos: adjuntos.filter((a) => a.kind === 'product').length,
            cantidadImagenes: adjuntos.filter((a) => a.kind === 'image').length,
            adjuntos: nombresDeAdjuntos,
            menciones: adjuntos.map((a) => `@${nombreDeMencion(a, adjuntos)}`),
            enSesion: !!sesionId,
            asistente,
          }),
        aplicar: (cambios) =>
          aplicarEnRedes(cambios, {
            // Si el agente igual escribió `@Image1` (es como redacta video), en la caja se
            // ve con el nombre del adjunto que ocuparía esa posición
            // (ADR-2026-10-04-las-menciones-de-video-se-ven-por-nombre).
            setPrompt: (v) =>
              setPrompt(
                ajustes.medio === 'video'
                  ? deImagenANombre(v, ordenDeReferencias(adjuntos, MAX_REFERENCIAS_VIDEO), adjuntos)
                  : v,
              ),
            setFormato: (slug) =>
              setAjustes((a) => (a.medio === 'video' ? { ...a, video: { ...a.video, formatSlug: slug } } : { ...a, imagen: { ...a.imagen, formatSlug: slug } })),
            setCalidadImagen: (v) => setAjustes((a) => ({ ...a, imagen: { ...a.imagen, calidad: v } })),
            setCantidad: (n) => setAjustes((a) => ({ ...a, imagen: { ...a.imagen, cantidad: n } })),
            setDuracion: (s) => setAjustes((a) => ({ ...a, video: { ...a.video, ajustes: { ...a.video.ajustes, durationSeconds: s } } })),
            setModo: (m) => setAjustes((a) => conModo(a, m)),
          }),
      }),
      [location.pathname, prompt, ajustes, adjuntos, nombresDeAdjuntos, sesionId, asistente],
    ),
  );

  // ── Render ──────────────────────────────────────────────────────────────
  // En el Asistente, el usuario "empezó" cuando escribe, adjunta algo o ya mandó un pedido:
  // la demo se achica para darle el lugar (no se va) y Cumbre se asoma a escuchar.
  const interactuando = asistente && (escribiendo || prompt.trim() !== '' || adjuntos.length > 0 || conversando);
  // La conversación con Cumbre ya empezó: la sesión tiene algún mensaje del usuario (las
  // generaciones de la caja van al lienzo, no al chat), o hay un pedido saliendo. Desde
  // ahí, en el medio va la conversación y la caja queda sola para escribir, sin el
  // interruptor ni las sugerencias (ADR-2026-10-12-la-conversacion-del-asistente-en-crear).
  const hayConversacion = esElChat && chat.mensajes.some((m) => m.role === 'user');
  const enConversacion = asistente && (hayConversacion || pedidoAlAsistente !== null);
  // Sin interruptor en la conversación, una sesión que ya charló con Cumbre abre en el
  // Asistente: si no, al volver a ella la conversación no se vería. Una vez por sesión, así
  // pasar después a Imagen o Video (con una sesión nueva) no rebota.
  const sesionAbiertaEnAsistenteRef = useRef<string | null>(null);
  useEffect(() => {
    if (!sesionId || !hayConversacion || acceso !== 'permitido') return;
    if (sesionAbiertaEnAsistenteRef.current === sesionId) return;
    sesionAbiertaEnAsistenteRef.current = sesionId;
    setAsistente(true);
  }, [sesionId, hayConversacion, acceso]);
  // "Agregar" en una foto de la conversación: con la conversación en el medio, la caja que
  // se ve es ÉSTA, así que la foto entra acá como referencia (la que viaja con el próximo
  // mensaje) y no a los adjuntos del chat, que en Crear no se ven
  // (ADR-2026-10-17-el-agregar-del-chat-va-a-la-caja-de-crear). En el Canva de la
  // computadora la conversación va en el panel del Agente, con su propia caja: ahí no.
  const cajaEsLaDeLaConversacion = enConversacion && !lienzoVisible;
  useEffect(() => {
    if (!cajaEsLaDeLaConversacion) return;
    const agregar: AgregarDelChat = (adjunto, origen) =>
      adjunto.kind === 'video' ? void sumarVideoDeReferencia(adjunto, origen) : volarAReferencia(adjunto, origen);
    registrarAgregarDelChat(agregar);
    return () => {
      if (leerAgregarDelChat() === agregar) registrarAgregarDelChat(null);
    };
  }, [cajaEsLaDeLaConversacion, volarAReferencia, sumarVideoDeReferencia]);
  // Tocar una foto del chat la abre en el visor ACÁ, sobre la conversación, en vez de
  // navegar a la galería de imágenes generadas: de allá, volver rearmaba Crear desde cero
  // (otra vista, la conversación arriba de todo) y mostraba un "Listo" que no iba
  // (ADR-2026-10-17-la-foto-del-chat-de-crear-se-abre-en-el-lugar). Con el lienzo del
  // Agente montado (el Canva) manda el visor de ese lienzo, que se registra solo.
  const [fotoDelChat, setFotoDelChat] = useState<string | null>(null);
  const lienzoDelAgenteMontado = lienzoVisible && vistaDeResultados === 'canva';
  useEffect(() => {
    if (lienzoDelAgenteMontado) return;
    const abrir = (imageId: string) => setFotoDelChat(imageId);
    registrarVisorDelLienzo(abrir);
    return () => {
      if (leerVisorDelLienzo() === abrir) registrarVisorDelLienzo(null);
    };
  }, [lienzoDelAgenteMontado]);
  const caja = (
    <CajaDeRedes
      sugiriendo={sugiriendo}
      prompt={prompt}
      onPromptChange={setPrompt}
      pedidoDeFoco={pedidoDeFoco}
      onGenerar={() => void handleGenerar()}
      falta={falta}
      generando={generando}
      adjuntos={adjuntos}
      onQuitarAdjunto={quitarAdjunto}
      onAbrirFuentes={setFuentes}
      ajustes={ajustes}
      onAjustes={setAjustes}
      onAyudaDePrompt={MOSTRAR_AYUDA_DE_PROMPT && acceso === 'permitido' ? ayudaDePrompt : undefined}
      onMejorarPrompt={() => void mejorarPrompt()}
      mejorando={mejorando}
      resultadoDeMejora={resultadoDeMejora}
      subiendo={subiendo}
      // La pastilla DEV sólo existe para `dev_tester`.
      proveedorForzado={proveedorForzado}
      onProveedorForzado={isDevTester ? setProveedorForzado : undefined}
      // El Asistente es el Agente: sólo con un plan que lo tenga (Pro/Ultra o dev).
      asistente={asistente}
      onAsistente={acceso === 'permitido' ? setAsistente : undefined}
      onEnviarAlAsistente={enviarAlAsistente}
      // La Inteligencia y el engranaje de la caja del Agente, los MISMOS (atados al chat de la
      // sesión), en lugar de Prompts (ADR-2026-10-13-inteligencia-y-ajustes-en-el-asistente).
      ajustesDelAsistente={
        <PastillasDeContenido
          preferencias={chat.preferencias}
          onCambiar={chat.setPreferencia}
          inteligencia={chat.inteligencia}
          onCambiarInteligencia={chat.setInteligencia}
          puedeSubirInteligencia={chat.puedeSubirInteligencia}
          compactas={!esEscritorio}
        />
      }
      // Cumbre se asoma en la portada; sobre los resultados taparía las cards.
      // Cumbre asoma sólo en la computadora: en el teléfono le sacaba lugar a la caja.
      conMascota={!lienzoVisible && esEscritorio}
      interactuando={interactuando}
      enConversacion={enConversacion}
      // Sobre las fotos de la cuadrícula, el interruptor y las sugerencias van con relleno
      // (ADR-2026-10-12-sobre-la-cuadricula-el-interruptor-y-las-sugerencias-van-llenos).
      sobreFotos={lienzoVisible && vistaDeResultados === 'cuadricula'}
      // El clip de la card "Animar imagen" sigue al rubro, como la portada.
      rubro={rubroDeLaPortada}
    />
  );
  // La caja con el foco en su TEXTO (no en un botón: tocar la pestaña Asistente no cuenta
  // como empezar a escribir). El blur espera un poco: tocar una sugerencia primero le saca
  // el foco al texto y recién después lo vuelve a poner.
  const seguirElFoco = {
    onFocus: (e: React.FocusEvent) => {
      if (!(e.target instanceof HTMLTextAreaElement)) return;
      if (blurRef.current) window.clearTimeout(blurRef.current);
      setEscribiendo(true);
    },
    onBlur: (e: React.FocusEvent) => {
      if (!(e.target instanceof HTMLTextAreaElement)) return;
      if (blurRef.current) window.clearTimeout(blurRef.current);
      blurRef.current = window.setTimeout(() => setEscribiendo(false), 220);
    },
  };

  const plantillas = (
    <PlantillasDeRedes
      medio={ajustes.medio}
      tipoVideo={ajustes.video.tipoVideo}
      onTipoVideo={(slug) => setAjustes((a) => ({ ...a, video: { ...a.video, tipoVideo: slug } }))}
      onElegirPlantilla={elegirPlantilla}
    />
  );

  // En el teléfono la pantalla sube sobre el lugar del header (sin "Volver" queda vacío):
  // el fondo llega hasta arriba en vez de dejar una franja del color del shell.
  return (
    // En el teléfono el pie (la caja) sube lo que tapa el teclado: iOS no achica el viewport
    // al abrirlo y la caja quedaba debajo (review atlas-code#179,
    // ADR-2026-10-14-la-caja-de-crear-sube-con-el-teclado).
    <div
      ref={raizRef}
      className="relative flex h-[calc(100dvh-env(safe-area-inset-top))] flex-col overflow-hidden max-md:-mt-[4.25rem] md:h-[100dvh]"
      style={tecladoAlto ? { paddingBottom: tecladoAlto } : undefined}
    >
      <VeloSoltarImagen visible={arrastrando} />
      {/* El visor de las fotos del chat: se monta acá pero se dibuja portaleado al `body`,
          como el de la home (ADR-2026-10-17-la-foto-del-chat-de-crear-se-abre-en-el-lugar). */}
      {fotoDelChat && (
        <GeneratedImagesHistory
          soloVisor={{
            imageId: fotoDelChat,
            brandId,
            onCerrar: () => setFotoDelChat(null),
            onError: () => setFotoDelChat(null),
            onGeneracionDelegada: alDelegar,
          }}
        />
      )}
      {/* Volver, en la fila de "Ver creaciones" / Cuadrícula: la misma pastilla que la tira
          de la sidebar en escritorio (chevron + logo). Lleva a la HOME (la misma ruta que la
          pestaña Inicio de la barra), no un paso atrás: dentro de una sesión el historial
          tiene los saltos entre sesiones y la conversación, y volver no salía de Crear
          (ADR-2026-10-13-volver-de-crear-va-a-la-home). */}
      <button
        type="button"
        onClick={() => navigate('/interfaz-v2')}
        aria-label="Volver"
        className="absolute left-4 top-4 z-20 inline-flex h-10 items-center gap-1 rounded-full bg-white pl-2 pr-3.5 shadow-[0_10px_30px_-10px_rgba(2,6,43,0.28)] ring-1 ring-black/[0.05] active:opacity-70 md:hidden"
      >
        <span aria-hidden="true" className="material-symbols-rounded text-[20px] text-black/50">
          chevron_left
        </span>
        <img src="/images/estudio-atlas-logo-solo.svg" alt="" className="h-[20px] w-auto" />
      </button>
      {/* Sin encabezado: la pantalla es la caja y el lienzo. En la esquina (con la sidebar
          en tira, al lado de su logo) sólo "Nueva sesión", y sólo en el CANVA: la sesión es
          lo que ese lienzo junta; la cuadrícula muestra todo lo de la empresa y las
          plantillas no dependen de la sesión, así que ahí no tiene sentido ofrecerla. */}
      {/* También sobre la conversación del Asistente: sin el interruptor, es la salida para
          empezar otra cosa (ADR-2026-10-12-la-conversacion-del-asistente-en-crear). En el
          teléfono va después de la pastilla de volver. */}
      {sesionId && ((lienzoVisible && vistaDeResultados === 'canva') || (!lienzoVisible && enConversacion)) && (
        <button
          type="button"
          onClick={() => void nuevaSesion()}
          style={{ left: esEscritorio ? (tiraDeSidebar ? ANCHO_LOGO_SIDEBAR_MINIMA : 0) + 16 : 88 }}
          className={`${BOTON_ESQUINA} absolute top-4 z-20 transition-[left] duration-300 ease-out`}
        >
          <Plus className="h-4 w-4" strokeWidth={2.25} aria-hidden="true" />
          Nueva sesión
        </button>
      )}

      {!sesionId ? null : lienzoVisible ? (
        <>
          {/* El lienzo ocupa TODO el alto y la caja flota encima, abajo: el canvas pasa
              por debajo de ella hasta el borde, como la grilla del Creador pasa por debajo
              de su caja (ADR-2026-09-08-el-chrome-del-creador-de-video-flota-sobre-la-grilla). */}
          <div ref={contenedorRef} className="relative min-h-0 flex-1">
            {vistaDeResultados === 'canva' ? (
              <div className="absolute inset-0 isolate z-0">
                {/* En el Canva del Asistente la caja no está (la conversación va en el panel):
                    la barra baja, horizontal y centrada, como en el resto de los lienzos
                    (ADR-2026-10-13-la-barra-del-canva-del-asistente-va-abajo). */}
                {esElChat && ancho > 0 && (
                  <LienzoDelAgente estado={estado} ancho={ancho} barra={asistente && esEscritorio ? 'abajo' : 'derecha'} alAgregar={agregarDelLienzo} />
                )}
              </div>
            ) : (
              // La grilla scrollea por debajo del toggle (arriba) y de la caja (abajo).
              // `isolate`: las cards traen botones con `z-index` propio (descargar, etc.)
              // y sin un contexto de apilado acá se dibujaban POR ENCIMA de la caja.
              <div className="absolute inset-0 isolate z-0 overflow-y-auto px-4 md:px-6" style={{ paddingTop: 72, paddingBottom: altoCaja + 28 }}>
                <PanelTandaCreaciones
                  tandas={tandasPanel}
                  companyId={companyId}
                  brandId={brandId ?? null}
                  recargarToken={recargarToken}
                  modoActual="redes"
                  // Cinco columnas fijas: a pantalla completa `auto` repartía en seis o
                  // siete cards chicas; cinco es lo que se pidió para el ancho entero.
                  // En el teléfono, dos (ADR-2026-10-09-asistente-en-crear).
                  columnas={esEscritorio ? 5 : 2}
                  filtro="todo"
                  onGeneracionDelegada={alDelegar}
                  // La card limpia: rótulo y acciones al pasar el mouse. "Editar" pone la
                  // creación de referencia en la caja con un prompt que lo dice.
                  accionesEnHover={{
                    // "Rehacer" sólo en las creaciones que guardaron con qué se hicieron.
                    puedeRehacer: (d) => leerReceta(d) !== null,
                    onRehacer: (d) => void rehacer(d),
                    // La foto se achica y vuela a "Añadir referencia"; al llegar entra como
                    // adjunto (ahí aparece su miniatura) y se escribe el prompt.
                    onEditar: (adjunto, origen) => {
                      if (adjunto.kind === 'video') {
                        // "Añadir como referencia" de un VIDEO: pasa a Video, respeta el
                        // tope de duración y es uno solo. No se escribe prompt: no es una
                        // edición, es estilo y ritmo.
                        void sumarVideoDeReferencia(adjunto, origen);
                        return;
                      }
                      volarAReferencia(adjunto, origen, () => setPrompt('Quisiera editar esta imagen'));
                    },
                  }}
                />
              </div>
            )}
            {/* En el Canva del Asistente, con el panel cerrado: un botón para volver a abrir la
                conversación, arriba a la derecha donde estaba (ADR-2026-10-13-reabrir-el-chat-del-asistente). */}
            {asistente && esEscritorio && !chat.abierto && (
              <button
                type="button"
                onClick={() => abrirChat({ contexto: contextoDeCrear, vista: 'panel' })}
                className={`${BOTON_ESQUINA} absolute right-4 top-4 z-20 transform transition-transform duration-300 hover:-translate-y-0.5 md:right-6`}
              >
                <MessageCircle className="h-4 w-4" strokeWidth={2} aria-hidden="true" />
                Abrir chat
              </button>
            )}
            {/* Cuadrícula | Canva: cómo se ve lo generado. En el Asistente no: ahí es sólo
                el Canva (ADR-2026-10-12-la-conversacion-del-asistente-en-crear). En el
                teléfono tampoco: el Canva es de computadora, así que quedaba una pastilla con
                una sola opción que no cambiaba nada, y su esquina es del chevron de la
                portada (ADR-2026-10-17-en-el-telefono-el-chevron-de-crear-va-arriba). */}
            {!asistente && esEscritorio && (
            <div
              role="tablist"
              aria-label="Vista de los resultados"
              className="absolute right-4 top-4 z-20 inline-flex h-10 items-center rounded-full bg-white p-1 shadow-[0_10px_30px_-10px_rgba(2,6,43,0.28)] ring-1 ring-black/[0.05] md:right-6"
            >
              {/* El Canva es de computadora: en el teléfono no hay lienzo que mover. */}
              {([
                { id: 'cuadricula', rotulo: 'Cuadrícula', Icono: LayoutGrid },
                { id: 'canva', rotulo: 'Canva', Icono: PanelsTopLeft },
              ] as const).filter((o) => esEscritorio || o.id === 'cuadricula').map(({ id, rotulo, Icono }) => {
                const activa = vistaDeResultados === id;
                return (
                  <button
                    key={id}
                    type="button"
                    role="tab"
                    aria-selected={activa}
                    onClick={() => setModoDeVista(id)}
                    className={`inline-flex h-8 items-center gap-1.5 rounded-full px-3.5 text-[13px] font-semibold transition-colors ${
                      activa ? 'bg-[#EEF0F5] text-brand-black' : 'text-[#02062B80] hover:text-brand-black'
                    }`}
                  >
                    <Icono className={`h-4 w-4 ${activa ? 'text-brand-primary' : ''}`} strokeWidth={2} aria-hidden="true" />
                    {rotulo}
                  </button>
                );
              })}
              {/* No es una vista: lleva al último proyecto del Estudio abierto desde esta
                  sesión, y el editor ofrece volver acá. */}
              {proyectoDelEstudio && (
                <button
                  type="button"
                  onClick={() => navigate(`/editor-video/${proyectoDelEstudio}`, { state: estadoDesdeRedes(location.pathname) })}
                  className="inline-flex h-8 items-center gap-1.5 rounded-full px-3.5 text-[13px] font-semibold text-[#02062B80] transition-colors hover:text-brand-black"
                >
                  <Clapperboard className="h-4 w-4" strokeWidth={2} aria-hidden="true" />
                  Estudio de video
                </button>
              )}
            </div>
            )}
            {/* "Plantillas", en la esquina inferior derecha: esconde los resultados y vuelve
                a la vista de la portada sin salir de la sesión. */}
            {/* Sólo un chevron, sin rótulo: con las plantillas ocultas
                (ADR-2026-10-05-crear-sin-plantillas) ya no lleva a ellas, sólo vuelve a la
                portada de la sesión (ADR-2026-10-05-el-boton-de-portada-es-un-chevron).
                En el teléfono va ARRIBA a la derecha, en la fila de la pastilla de volver y
                con su mismo alto: abajo quedaba encima del botón Generar de la caja
                (ADR-2026-10-17-en-el-telefono-el-chevron-de-crear-va-arriba). */}
            <button
              type="button"
              onClick={() => {
                if (asistente) cerrarPanelDelAsistente();
                cambiarVista('plantillas');
              }}
              aria-label="Volver a la portada"
              title="Volver a la portada"
              className="absolute right-4 top-4 z-20 grid h-10 w-10 place-items-center rounded-full md:bottom-6 md:right-6 md:top-auto bg-white text-brand-black shadow-[0_10px_30px_-10px_rgba(2,6,43,0.28)] ring-1 ring-black/[0.05] transition-colors hover:bg-[#F3F4F8]"
            >
              <ChevronDown className="h-5 w-5" strokeWidth={2} aria-hidden="true" />
            </button>
            {/* En el Canva del Asistente la caja es la del panel del Agente, a la derecha:
                dos cajas para la misma conversación confundían. En el teléfono no hay panel
                ni Canva: la caja se queda. */}
            {!(asistente && esEscritorio) && (
            <div className="pointer-events-none absolute inset-x-0 bottom-0 z-10 px-4 pb-4 md:px-6 md:pb-5">
              <div ref={cajaRef} className="pointer-events-auto mx-auto max-w-[1100px]" style={{ viewTransitionName: 'caja-de-redes' }} {...seguirElFoco}>
                {caja}
              </div>
            </div>
            )}
          </div>
        </>
      ) : (
        <div className="relative min-h-0 flex-1">
          {/* El fondo es UNO para la portada y la conversación: montado acá, no se rehace al
              pasar de una a la otra (antes se remontaba y la aurora arrancaba de cero, que se
              veía como un parpadeo) — ADR-2026-10-12-la-conversacion-del-asistente-en-crear-2. */}
          <FondoDeCrear atenuado={enConversacion} />
          {enConversacion ? (
            // La conversación con Cumbre, en el medio y sobre el mismo fondo azul: los MISMOS
            // mensajes que el Agente (`MensajesDeLaConversacion`, con su scroll, Cumbre al pie
            // y la card activa), y abajo la caja de Crear para seguir escribiendo
            // (ADR-2026-10-12-la-conversacion-del-asistente-en-crear). Los textos en globos
            // blancos, que sobre el azul se despegan.
            <div key="conversacion" className="relative flex h-full flex-col">
              <div className="crear-conversa relative z-10 flex min-h-0 flex-1 flex-col pt-14 md:pt-4">
                <MensajesDeLaConversacion
                  anchoColumna="max-w-3xl"
                  pedidoDeIrAlFondo={pedidoDeIrAlFondo}
                  globosBlancos
                  onSugerencia={completarConSugerencia}
                />
              </div>
              <div className="relative z-10 shrink-0 px-4 pb-4 pt-2 md:px-6 md:pb-6">
                <div className="mx-auto w-full max-w-3xl" style={{ viewTransitionName: 'caja-de-redes' }} {...seguirElFoco}>
                  {caja}
                </div>
              </div>
            </div>
          ) : (
          <div className={`relative h-full overflow-y-auto px-4 md:px-6 ${MOSTRAR_PLANTILLAS ? 'pb-16' : ''}`}>
            {/* La primera pantalla: la frase y la caja a MEDIA altura, centradas; las
                plantillas arrancan debajo del pliegue y se llega scrolleando. */}
            {/* `-mx-6 px-6`: el fondo llega a los bordes del scroller; el contenido conserva
                su aire. */}
            {/* Sin plantillas debajo, la portada ocupa toda la altura (nada asoma bajo el pliegue). */}
            <div
              className={`relative -mx-4 flex flex-col px-4 pb-4 pt-16 md:-mx-6 md:justify-center md:px-6 md:py-10 ${
                MOSTRAR_PLANTILLAS ? 'min-h-[calc(100%-6rem)]' : 'min-h-full'
              }`}
            >
              {/* El degradado de la marca (`FondoDeCrear`) va afuera, compartido con la
                  conversación del Asistente. */}
              {/* Lo de arriba cambia con la pestaña: se cruza con un fundido corto y la caja de
                  abajo se acomoda en altura en vez de saltar (ADR-2026-10-09-asistente-en-crear-2). */}
              {/* En el teléfono, la caja (con el interruptor y las sugerencias) va abajo, a mano
                  del pulgar, y lo de arriba queda centrado en el lugar que sobra: un aire
                  flexible antes y otro después (ADR-2026-10-09-asistente-en-crear-7). */}
              <div aria-hidden="true" className="flex-1 md:hidden" />
              <AlturaSuave clave={asistente ? 'asistente' : 'crear'} className="relative z-10">
                {asistente ? (
                  // Asistente: arriba, Cumbre muestra lo que se le puede pedir. Cuando el
                  // usuario empieza a usar la caja se achica a una pastilla.
                  <div key="asistente" className="crear-cambia mb-5 md:mb-8">
                    <DemoDelAsistente minimizada={interactuando} conversando={conversando} rubro={rubroDeLaPortada} />
                  </div>
                ) : (
                  <div key="crear" className="crear-cambia">
                    <h2 className="mb-5 text-center text-[26px] font-bold leading-[1.15] tracking-[-0.02em] text-brand-black md:mb-6 md:text-[34px]">
                      ¿Qué creamos hoy?
                    </h2>
                    {/* Entre el título y la caja, el abanico de plantillas que va pasando solo; con
                        Video pasa a los clips (el cambio de medio lo anima el carrusel). En el
                        teléfono, compacto: tres a la vista en vez de cinco. */}
                    <CarruselDePlantillas medio={ajustes.medio} compacto={!esEscritorio} rubro={rubroDeLaPortada} className="mb-5 md:mb-6" />
                  </div>
                )}
              </AlturaSuave>
              <div aria-hidden="true" className="flex-1 md:hidden" />
              <div className="relative z-10 mx-auto w-full max-w-[1100px]" style={{ viewTransitionName: 'caja-de-redes' }} {...seguirElFoco}>
                {caja}
              </div>
            </div>
            {MOSTRAR_PLANTILLAS && <div>{plantillas}</div>}
          </div>
          )}
          {/* "Ver creaciones", arriba a la derecha (donde en los resultados va el toggle
              Cuadrícula | Canva): pasa a los resultados con la transición de vista —la caja
              viaja de su lugar al pie— y abre en la vista que se usó la última vez. */}
          {/* En el Asistente lleva al Canva (con la conversación en el panel), y sólo cuando
              Cumbre ya generó algo: antes no hay nada que ver. */}
          {/* En el Asistente, "Historial" en lugar de "Ver creaciones": las últimas
              conversaciones, para volver a una (ADR-2026-10-13-historial-en-el-asistente). */}
          {sesionId && asistente && (
            <div className="absolute right-4 top-4 z-20 flex items-center gap-2 md:right-6">
              {/* Con algo ya generado, "Ver creaciones" vuelve al Canva con la conversación en el
                  panel: es el camino de vuelta después de agrandar
                  (ADR-2026-10-13-agrandar-el-asistente-es-la-conversacion-en-el-medio). */}
              {/* Dice lo que hace —el lienzo con el chat al costado— y lleva el ícono de achicar,
                  el opuesto del agrandar del panel (ADR-2026-10-13-volver-al-lienzo-desde-la-conversacion). */}
              {esEscritorio && estado.tandas.length > 0 && (
                <button
                  type="button"
                  onClick={abrirCanvaDelAsistente}
                  title="Ver el lienzo con el chat al costado"
                  className={`${BOTON_ESQUINA} transform transition-transform duration-300 hover:-translate-y-0.5`}
                >
                  <Minimize2 className="h-4 w-4" strokeWidth={2} aria-hidden="true" />
                  Volver al lienzo
                </button>
              )}
              <HistorialDeSesiones
                actual={sesionId}
                soloConversaciones
                onElegir={(id) => navigate(rutaDeSesion(id), { state: { conversacion: true } })}
                claseDelBoton={`${BOTON_ESQUINA} transform transition-transform duration-300 hover:-translate-y-0.5`}
                className="relative"
              />
            </div>
          )}
          {/* En Imagen y Video, sólo "Ver creaciones": el historial de chats es del Asistente
              (ADR-2026-10-20-el-historial-solo-en-el-asistente). */}
          {sesionId && !asistente && (
            <div className="absolute right-4 top-4 z-20 flex items-center gap-2 md:right-6">
              <button
                type="button"
                onClick={() => cambiarVista('lienzo')}
                className={`${BOTON_ESQUINA} transform transition-transform duration-300 hover:-translate-y-0.5`}
              >
                <LayoutGrid className="h-4 w-4" strokeWidth={2} aria-hidden="true" />
                Ver creaciones
              </button>
            </div>
          )}
        </div>
      )}

      {/* El selector del Agente, en la pestaña que pidió el botón. `onAdjuntar` lo saca
          del chat: lo elegido entra a ESTA caja (ADR-2026-09-27-editar-clip-agrega-con-el-selector-del-agente).
          Moodboards sólo en el Asistente, que pasa la caja al chat: una carpeta la mira el
          agente; Imagen y Video no sabrían qué hacer con ella
          (ADR-2026-10-18-el-moodboard-en-el-selector-de-fuentes). */}
      <ModalDeFuentes abierto={fuentes !== null} onCerrar={() => setFuentes(null)} onAdjuntar={alAdjuntar} multiple adjuntosActuales={adjuntos} onQuitar={quitarAdjunto} pestanaInicial={fuentes ?? 'productos'} subidaEnDosTarjetas conMoodboards={asistente} />

      <SubscriptionLimitModal isOpen={limite !== null} onClose={() => setLimite(null)} type={limite ?? 'plan-limit-reached'} />

      {lockVideo &&
        createPortal(
          <div className="fixed inset-0 z-[140] flex items-center justify-center p-6" role="dialog" aria-modal="true" aria-label={lockVideo.title}>
            <button type="button" aria-label="Cerrar" onClick={() => setLockVideo(null)} className="absolute inset-0 h-full w-full cursor-default bg-black/45 backdrop-blur-[2px]" />
            <div className="animate-fade-in relative z-10 w-full max-w-[420px] rounded-2xl bg-white p-6 text-center shadow-[0_24px_80px_rgba(16,24,45,0.28)]">
              <Lock className="mx-auto h-7 w-7 text-[#02062B]/[0.4]" aria-hidden="true" />
              <p className="mt-3 text-[16px] font-semibold text-brand-black">{lockVideo.title}</p>
              <p className="mt-1.5 text-[14px] text-[#3C3C43]/60">{lockVideo.message}</p>
              <button
                type="button"
                onClick={() => navigate('/subscription')}
                className="mt-5 rounded-full bg-brand-primary py-2.5 px-5 text-[14.5px] font-semibold text-white transition-colors hover:bg-brand-primary-hover"
              >
                Ver planes
              </button>
            </div>
          </div>,
          document.body,
        )}

      {error &&
        createPortal(
          <button type="button" onClick={() => setError(null)} className="fixed bottom-6 left-1/2 z-[250] -translate-x-1/2 px-4">
            <span className="flex items-center gap-2 rounded-2xl bg-brand-black py-3 px-6 text-sm text-white shadow-xl">
              <AlertCircle className="h-4 w-4 shrink-0 text-red-400" />
              {error}
            </span>
          </button>,
          document.body,
        )}
    </div>
  );
};

export default PantallaRedes;
